import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, FileText, Check, X, AlertCircle } from "lucide-react";
import { Mail, MailAttachment } from "./types";
import { useQuery } from "@tanstack/react-query";

interface BulkParseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmails: Mail[];
  onComplete: () => void;
}

interface AttachmentStatus {
  id: string;
  fileName: string;
  emailSubject: string;
  status: "pending" | "processing" | "success" | "error";
  error?: string;
}

export function BulkParseDialog({
  open,
  onOpenChange,
  selectedEmails,
  onComplete,
}: BulkParseDialogProps) {
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [attachmentStatuses, setAttachmentStatuses] = useState<AttachmentStatus[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch jobs
  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["jobs-for-bulk-parse"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("id, title, clients(name)")
        .in("status", ["draft", "published"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  // Get all resume attachments from selected emails
  const resumeAttachments = selectedEmails.flatMap((email) => {
    const attachments = email.inbound_email_attachments || [];
    return attachments
      .filter((a) => /\.(pdf|docx?|doc)$/i.test(a.file_name))
      .filter((a) => a.file_size && a.file_size > 0) // Only include valid attachments
      .map((a) => ({
        ...a,
        emailSubject: email.subject || "(No Subject)",
        emailFromName: email.from_name,
        emailFromEmail: email.from_email.match(/<(.+)>$/)?.[1] || email.from_email,
      }));
  });

  const handleProcess = async () => {
    if (resumeAttachments.length === 0) {
      toast({
        title: "No valid resumes",
        description: "No valid resume attachments found in selected emails.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);

    // Initialize statuses
    const initialStatuses: AttachmentStatus[] = resumeAttachments.map((a) => ({
      id: a.id,
      fileName: a.file_name,
      emailSubject: a.emailSubject,
      status: "pending",
    }));
    setAttachmentStatuses(initialStatuses);

    // Get current user's app_users id
    const { data: appUser } = await supabase
      .from("app_users")
      .select("id")
      .eq("user_id", user?.id)
      .maybeSingle();

    let successCount = 0;
    let errorCount = 0;

    // Process each attachment
    for (const attachment of resumeAttachments) {
      setAttachmentStatuses((prev) =>
        prev.map((s) =>
          s.id === attachment.id ? { ...s, status: "processing" } : s
        )
      );

      try {
        // Download the attachment from storage
        const { data: fileData, error: downloadError } = await supabase.storage
          .from("inbound-email-attachments")
          .download(attachment.storage_path);

        if (downloadError) throw downloadError;

        // Parse name from email sender or filename
        let firstName = "Unknown";
        let lastName = "Candidate";

        if (attachment.emailFromName) {
          const nameParts = attachment.emailFromName.split(/\s+/);
          firstName = nameParts[0] || firstName;
          lastName = nameParts.slice(1).join(" ") || lastName;
        } else {
          const fileNameParts = attachment.file_name
            .replace(/\.(pdf|docx?)$/i, "")
            .split(/[_\-\s]+/);
          firstName = fileNameParts[0] || firstName;
          lastName = fileNameParts.slice(1).join(" ") || lastName;
        }

        // Create candidate record
        const { data: candidate, error: candidateError } = await supabase
          .from("candidates")
          .insert({
            first_name: firstName,
            last_name: lastName,
            email_primary: attachment.emailFromEmail || null,
          })
          .select()
          .single();

        if (candidateError) throw candidateError;

        // Upload file to resumes storage bucket
        const storagePath = `${candidate.id}/${Date.now()}_${attachment.file_name}`;
        const { error: uploadError } = await supabase.storage
          .from("resumes")
          .upload(storagePath, fileData);

        if (uploadError) throw uploadError;

        // Create resume record
        const { data: resumeRecord, error: resumeError } = await supabase
          .from("candidate_resumes")
          .insert({
            candidate_id: candidate.id,
            original_filename: attachment.file_name,
            storage_path: storagePath,
            file_size: attachment.file_size,
          })
          .select()
          .single();

        if (resumeError) throw resumeError;

        // Create candidate application if job is selected
        if (selectedJobId) {
          await supabase.from("candidate_applications").insert({
            candidate_id: candidate.id,
            job_id: selectedJobId,
            status: "applied",
          });
        }

        // Trigger deep parsing (fire and forget)
        supabase.functions
          .invoke("trigger-deep-parse", {
            body: {
              candidateId: candidate.id,
              resumeId: resumeRecord.id,
              fileName: attachment.file_name,
              jobId: selectedJobId || null,
              uploadedBy: appUser?.id,
            },
          })
          .catch((err) => console.error("Failed to trigger parsing:", err));

        setAttachmentStatuses((prev) =>
          prev.map((s) =>
            s.id === attachment.id ? { ...s, status: "success" } : s
          )
        );
        successCount++;
      } catch (error: any) {
        console.error("Error processing attachment:", error);
        setAttachmentStatuses((prev) =>
          prev.map((s) =>
            s.id === attachment.id
              ? { ...s, status: "error", error: error.message }
              : s
          )
        );
        errorCount++;
      }
    }

    setProcessing(false);

    toast({
      title: "Bulk processing complete",
      description: `${successCount} candidates created, ${errorCount} failed.`,
    });

    if (successCount > 0) {
      onComplete();
    }
  };

  const completedCount = attachmentStatuses.filter(
    (s) => s.status === "success" || s.status === "error"
  ).length;
  const progress =
    attachmentStatuses.length > 0
      ? (completedCount / attachmentStatuses.length) * 100
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Candidates from Resumes</DialogTitle>
          <DialogDescription>
            Found {resumeAttachments.length} resume attachment(s) in{" "}
            {selectedEmails.length} selected email(s). Select a job to associate
            them with.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Job selection */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Link to Job (optional)
            </label>
            <Select
              value={selectedJobId}
              onValueChange={(val) =>
                setSelectedJobId(val === "none" ? "" : val)
              }
              disabled={loadingJobs || processing}
            >
              <SelectTrigger>
                {loadingJobs ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading jobs...
                  </span>
                ) : (
                  <SelectValue placeholder="Select a job (optional)" />
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No job selected</SelectItem>
                {jobs
                  .filter((job) => job.id)
                  .map((job: any) => {
                    const clientName = Array.isArray(job.clients) 
                      ? job.clients[0]?.name 
                      : job.clients?.name;
                    return (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} {clientName && `- ${clientName}`}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>

          {/* Attachments list */}
          {attachmentStatuses.length > 0 ? (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <ScrollArea className="h-48 border rounded-md p-2">
                {attachmentStatuses.map((status) => (
                  <div
                    key={status.id}
                    className="flex items-center gap-2 py-1.5 border-b last:border-0"
                  >
                    {status.status === "pending" && (
                      <div className="h-4 w-4 rounded-full bg-muted" />
                    )}
                    {status.status === "processing" && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    {status.status === "success" && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                    {status.status === "error" && (
                      <X className="h-4 w-4 text-destructive" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{status.fileName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {status.emailSubject}
                      </p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
          ) : resumeAttachments.length > 0 ? (
            <div className="border rounded-md p-3">
              <p className="text-sm font-medium mb-2">Resumes to process:</p>
              <ScrollArea className="h-32">
                {resumeAttachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 py-1">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm truncate">{a.file_name}</span>
                  </div>
                ))}
              </ScrollArea>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 border rounded-md text-muted-foreground">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">
                No valid resume attachments found in selected emails. Make sure
                the emails have PDF or Word document attachments that were
                properly saved.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProcess}
              disabled={processing || resumeAttachments.length === 0}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Create ${resumeAttachments.length} Candidate(s)`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
